import { I18nProvider } from '@lingui/react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FormTranslationsProvider } from '../-components/form-translations-provider';
import { useStartDonationCount } from './-components/donation-count';
import { NotFoundPage } from './-components/not-found';

const DM_SANS_URL = '/dm-sans-latin.woff2';

/**
 * Kept in step with the latin `@font-face` in `-styles/theme.css`, which is the same declaration
 * arriving later, through the stylesheet. Both are needed and neither is redundant: the stylesheet's
 * is what the rest of the app sees, and this one is what makes the face known before the stylesheet
 * has loaded. Same `unicode-range`, so the browser treats them as one face rather than two.
 */
const DM_SANS_FACE = [
	'@font-face{',
	"font-family:'DM Sans';",
	'font-style:normal;',
	'font-weight:400 700;',
	'font-display:swap;',
	`src:url('${DM_SANS_URL}') format('woff2');`,
	'unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,',
	'U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;',
	'}',
].join('');

export const Route = createFileRoute('/new')({
	// The same treatment `__root` gives Rethink Sans on the current site, for the redesign's DM Sans: the
	// face is DECLARED in the head, and the file it points at is preloaded, so the browser starts
	// fetching it while it is still parsing the document instead of after app.css has arrived and the
	// first heading has asked for it. Without that the page paints in the fallback and re-paints in DM
	// Sans a moment later, and every line that changed width shifts — the CLS the redesign was showing.
	//
	// Declared here rather than in `__root` because it is this route group's font: the portals, the
	// donor app and the backoffice all share that head and none of them uses DM Sans. The latin-ext
	// subset is left to `theme.css` and loaded on demand — it only carries accents no locale we serve
	// needs on first paint.
	head: () => ({
		links: [{ rel: 'preload', href: DM_SANS_URL, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' }],
		styles: [{ children: DM_SANS_FACE }],
	}),
	component: RouteComponent,
	// The `/new/$` catch-all covers a URL matching no route; this covers the other way in — a
	// `notFound()` raised by a page that does exist. Without it both fall through to the root's own
	// notFoundComponent, which is the PORTAL 404, chrome and all.
	notFoundComponent: NotFoundComponent,
});

/** Wrapped in the provider for the same reason RouteComponent is — see the note inside it. */
function NotFoundComponent() {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<NotFoundPage />
		</I18nProvider>
	);
}

function RouteComponent() {
	// Request-scoped Lingui instance from router context — see www/route.tsx.
	const { i18n } = Route.useRouteContext();
	// The live-donations count belongs to the whole visit: started here, it keeps running across
	// pages, so the odometer on Solutions reflects the time spent anywhere on the site.
	useStartDonationCount();
	return (
		<I18nProvider i18n={i18n}>
			<FormTranslationsProvider>
				<Outlet />
			</FormTranslationsProvider>
		</I18nProvider>
	);
}
